import { ScriptKeyPreset } from './preset';

interface ScriptElementAttrs {
  async?: boolean;
  defer?: boolean;
  crossOrigin?: 'anonymous' | 'use-credentials';
  integrity?: string;
  referrerPolicy?: ReferrerPolicy;
  type?: string;
  noModule?: boolean;
}

export interface LoadScriptOptions extends ScriptElementAttrs {
  src: string;
  /**
   * 一般情况下，你不需要传 key 参数，默认情况下将自动使用 src 作为 key
   * 除非你有多个相同内容但链接不同的脚本副本需要加载，此时可使用 key 为他们制定一个相同的 key，避免重复引入
   */
  key?: string;
}

interface ScriptState {
  promise: Promise<void>;
  element?: HTMLScriptElement;
}

export class SingletonScriptManager {
  private static instance: SingletonScriptManager | null = null;
  private scriptCache: Map<string, ScriptState>;
  private isBrowser: boolean;

  private constructor() {
    this.scriptCache = new Map();
    this.isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  static get ScriptKeyPreset() {
    return ScriptKeyPreset;
  };

  static getInstance(): SingletonScriptManager {
    if (!SingletonScriptManager.instance) {
      SingletonScriptManager.instance = new SingletonScriptManager();
    }
    return SingletonScriptManager.instance;
  }

  /**
   * 加载脚本，返回一个 Promise，脚本加载失败时将会 reject。
   * 若您不希望脚本加载失败时影响后续代码执行，请自行 catch 错误！
   */
  load(options: LoadScriptOptions): Promise<void> {
    const { src, key, ...scriptAttrs } = options;
    const scriptKey = key || src;

    if (!this.isBrowser) {
      return Promise.reject(
        new Error('[ScriptLoader] This function can only be used in browser environment'),
      );
    }

    if (!src) {
      return Promise.reject(new Error('[ScriptLoader] "src" is required'));
    }

    const cached = this.scriptCache.get(scriptKey);
    if (cached) {
      return cached.promise;
    }

    const script = document.createElement('script');
    Object.assign(script, scriptAttrs);
    script.src = src;
    script.dataset.scriptKey = scriptKey;

    const promise = new Promise<void>((resolve, reject) => {
      script.addEventListener('load', () => {
        script.dataset.scriptLoaded = 'true';
        resolve();
      });

      script.addEventListener('error', () => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
        this.scriptCache.delete(scriptKey);
        reject(new Error(`[ScriptLoader] Failed to load script: ${src}`));
      });
    });

    this.scriptCache.set(scriptKey, {
      promise,
      element: script,
    });

    document.head.appendChild(script);

    return promise;
  }

  isLoaded(key: string): boolean {
    if (!this.isBrowser) {
      return false;
    }
    return this.scriptCache.has(key);
  }

  remove(key: string): boolean {
    if (!this.isBrowser) {
      return false;
    }

    const cached = this.scriptCache.get(key);
    if (!cached) {
      return false;
    }

    if (cached.element?.parentNode) {
      cached.element.parentNode.removeChild(cached.element);
    }

    this.scriptCache.delete(key);
    return true;
  }

  clear(): void {
    if (!this.isBrowser) {
      return;
    }

    this.scriptCache.forEach((state) => {
      if (state.element?.parentNode) {
        state.element.parentNode.removeChild(state.element);
      }
    });

    this.scriptCache.clear();
  }
}
